import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import Login from '@/pages/Login';
import Onboarding from '@/pages/Onboarding';
import AccountSetup from '@/pages/AccountSetup';
import HospitalLogin from '@/pages/HospitalLogin';
import DashboardLayout from '@/components/DashboardLayout';
import AdminDashboard from '@/pages/AdminDashboard';
import DoctorDashboard from '@/pages/DoctorDashboard';
import NurseDashboard from '@/pages/NurseDashboard';
import ReceptionistDashboard from '@/pages/ReceptionistDashboard';
import PatientDashboard from '@/pages/PatientDashboard';
import Patients from '@/pages/Patients';
import PatientDetails from '@/pages/PatientDetails';
import Appointments from '@/pages/Appointments';
import MedicalRecords from '@/pages/MedicalRecords';
import Admissions from '@/pages/Admissions';
import Billing from '@/pages/Billing';
import FixedChargeMaster from '@/pages/FixedChargeMaster';
import Notifications from '@/pages/Notifications';
import DischargeSummary from '@/pages/DischargeSummary';
import DischargeAnalytics from '@/pages/DischargeAnalytics';
import LabDashboard from '@/pages/LabDashboard';
import LabEntryForm from '@/pages/LabEntryForm';
import VisitDashboard from '@/pages/VisitDashboard';
import VisitDetails from '@/pages/VisitDetails';
import Prescriptions from '@/pages/Prescriptions';
import AuditHistory from '@/pages/AuditHistory';
import AIScribe from '@/pages/AIScribe';
import UserManagement from '@/pages/UserManagement';
import ReferringDoctors from '@/pages/ReferringDoctors';
import MessageTemplates from '@/pages/MessageTemplates';
import CommunicationLogs from '@/pages/CommunicationLogs';
import ReferralAnalytics from '@/pages/ReferralAnalytics';
import LabOrders from '@/pages/LabOrders';
import SecuritySettings from '@/pages/SecuritySettings';
import SecurityAlerts from '@/pages/SecurityAlerts';
import ComplianceDashboard from '@/pages/ComplianceDashboard';
// Platform Admin imports
import PlatformLogin from '@/pages/PlatformLogin';
import PlatformLayout from '@/components/PlatformLayout';
import PlatformDashboard from '@/pages/PlatformDashboard';
import HospitalManagement from '@/pages/HospitalManagement';
import FeatureFlags from '@/pages/FeatureFlags';
import SystemHealth from '@/pages/SystemHealth';
import PlatformAuditLogs from '@/pages/PlatformAuditLogs';
// Multi-Tenant Portals
import MultiTenantPatientPortal from '@/pages/MultiTenantPatientPortal';
import ProviderManagement from '@/pages/ProviderManagement';
import ProviderPortalV2 from '@/pages/ProviderPortalV2';
import ReferralTriage from '@/pages/ReferralTriage';
import SendMessage from '@/pages/SendMessage';
import ResetPassword from '@/pages/ResetPassword';
import UnlockAccount from '@/pages/UnlockAccount';
import BrandingSettings from '@/pages/BrandingSettings';
import QuickRegistration from '@/pages/QuickRegistration';
import FHIRViewer from '@/pages/FHIRViewer';
import AdminUtilities from '@/pages/AdminUtilities';
import '@/App.css';

// Hospital Branding Context
export const BrandingContext = createContext(null);
export const useBranding = () => useContext(BrandingContext);

// Role-based dashboard mapping
const DASHBOARD_BY_ROLE = {
  admin: AdminDashboard,
  doctor: DoctorDashboard,
  nurse: NurseDashboard,
  receptionist: ReceptionistDashboard,
  reception: ReceptionistDashboard,  // New role
  lab_tech: LabDashboard,            // New role - uses Lab Dashboard
  patient: PatientDashboard
};

function App() {
  const [user, setUser] = useState(null);
  const [platformUser, setPlatformUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for platform admin session
    const platformToken = localStorage.getItem('platform_token');
    const platformUserData = localStorage.getItem('platform_user');
    if (platformToken && platformUserData) {
      setPlatformUser(JSON.parse(platformUserData));
    }

    // Check for regular user session
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const hospitalData = localStorage.getItem('hospital');
    if (token && userData) {
      setUser(JSON.parse(userData));
      if (hospitalData) {
        setHospital(JSON.parse(hospitalData));
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token, hospitalInfo = null) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (hospitalInfo) {
      localStorage.setItem('hospital', JSON.stringify(hospitalInfo));
      setHospital(hospitalInfo);
    }
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('hospital');
    setUser(null);
    setHospital(null);
  };

  const handlePlatformLogin = (userData, token) => {
    localStorage.setItem('platform_token', token);
    localStorage.setItem('platform_user', JSON.stringify(userData));
    setPlatformUser(userData);
  };

  const handlePlatformLogout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_user');
    setPlatformUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  // Platform Admin Routes (separate from hospital routes)
  // Check if current path is platform-admin or portal routes
  const isPlatformRoute = window.location.pathname.startsWith('/platform-admin');
  const isProviderPortalV2Route = window.location.pathname.startsWith('/provider-portal-v2');
  const isCommonPortalRoute = window.location.pathname.startsWith('/portal');
  
  // Multi-Tenant Provider Portal V2 route
  if (isProviderPortalV2Route) {
    return (
      <>
        <BrowserRouter>
          <Routes>
            <Route path="/provider-portal-v2" element={<ProviderPortalV2 />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </>
    );
  }
  
  // Common Multi-Tenant Patient Portal route (portal.zesta.care)
  if (isCommonPortalRoute) {
    return (
      <>
        <BrowserRouter>
          <Routes>
            <Route path="/portal/*" element={<MultiTenantPatientPortal />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </>
    );
  }
  
  if (isPlatformRoute) {
    if (!platformUser) {
      return (
        <>
          <BrowserRouter>
            <Routes>
              <Route path="/platform-admin" element={<PlatformLogin onLogin={handlePlatformLogin} />} />
              <Route path="/platform-admin/*" element={<Navigate to="/platform-admin" />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </>
      );
    }

    return (
      <>
        <BrowserRouter>
          <Routes>
            <Route path="/platform-admin" element={<PlatformLayout user={platformUser} onLogout={handlePlatformLogout} />}>
              <Route index element={<PlatformDashboard />} />
              <Route path="hospitals" element={<HospitalManagement />} />
              <Route path="features" element={<FeatureFlags />} />
              <Route path="health" element={<SystemHealth />} />
              <Route path="audit-logs" element={<PlatformAuditLogs />} />
              <Route path="settings" element={<div className="text-white">Platform Settings (Coming Soon)</div>} />
            </Route>
            <Route path="*" element={<Navigate to="/platform-admin" />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Regular Hospital User Routes
  if (!user) {
    return (
      <>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/login/:slug" element={<HospitalLogin onLogin={handleLogin} />} />
            <Route path="/setup/:token" element={<AccountSetup />} />
            <Route path="/onboard" element={<Onboarding />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unlock-account" element={<UnlockAccount />} />
            {/* Deprecated single-tenant portals removed - redirect to multi-tenant versions */}
            <Route path="/patient-portal/*" element={<Navigate to="/portal" />} />
            <Route path="/provider-portal/*" element={<Navigate to="/provider-portal-v2" />} />
            <Route path="/provider-portal-v2" element={<ProviderPortalV2 />} />
            <Route path="/platform-admin" element={<Navigate to="/platform-admin" />} />
            <Route path="/platform-admin/*" element={<Navigate to="/platform-admin" />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </>
    );
  }

  // Get dashboard component based on role
  const DashboardComponent = DASHBOARD_BY_ROLE[user.role] || PatientDashboard;

  return (
    <ErrorBoundary>
      <BrandingContext.Provider value={hospital}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<DashboardLayout user={user} onLogout={handleLogout} hospital={hospital} />}>
            <Route index element={<DashboardComponent />} />
            <Route path="patients" element={<Patients />} />
            <Route path="patients/:patientId" element={<PatientDetails />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="medical-records" element={<MedicalRecords />} />
            <Route path="admissions" element={<Admissions />} />
            <Route path="discharge-summary" element={<DischargeSummary />} />
            <Route path="discharge-analytics" element={<DischargeAnalytics />} />
            <Route path="lab-entries" element={<LabDashboard />} />
            <Route path="lab-entries/new" element={<LabEntryForm />} />
            <Route path="lab-entries/:entryId" element={<LabEntryForm />} />
            <Route path="visits" element={<VisitDashboard />} />
            <Route path="visits/:visitId" element={<VisitDetails />} />
            <Route path="prescriptions" element={<Prescriptions />} />
            <Route path="prescriptions/new" element={<Prescriptions />} />
            <Route path="prescriptions/:prescriptionId" element={<Prescriptions />} />
            <Route path="billing" element={<Billing />} />
            <Route path="fixed-charges" element={<FixedChargeMaster />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="audit-history" element={<AuditHistory />} />
            <Route path="ai-scribe" element={<AIScribe />} />
            <Route path="user-management" element={<UserManagement />} />
            <Route path="referring-doctors" element={<ReferringDoctors />} />
            <Route path="message-templates" element={<MessageTemplates />} />
            <Route path="communication-logs" element={<CommunicationLogs />} />
            <Route path="send-message" element={<SendMessage />} />
            <Route path="referral-analytics" element={<ReferralAnalytics />} />
            <Route path="lab-orders" element={<LabOrders />} />
            <Route path="provider-management" element={<ProviderManagement />} />
            <Route path="referral-triage" element={<ReferralTriage />} />
            <Route path="security-settings" element={<SecuritySettings />} />
            <Route path="security-alerts" element={<SecurityAlerts />} />
            <Route path="compliance-dashboard" element={<ComplianceDashboard />} />
            <Route path="branding-settings" element={<BrandingSettings />} />
            <Route path="quick-registration" element={<QuickRegistration />} />
            <Route path="fhir-viewer" element={<FHIRViewer />} />
            <Route path="admin-utilities" element={<AdminUtilities />} />
          </Route>
          <Route path="/onboard" element={<Onboarding />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/unlock-account" element={<UnlockAccount />} />
          <Route path="/platform-admin" element={<Navigate to="/platform-admin" />} />
          <Route path="/platform-admin/*" element={<Navigate to="/platform-admin" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </BrandingContext.Provider>
    </ErrorBoundary>
  );
}

export default App;